import {
  DataGridIssueSubtypeByTypeManagerFilter,
  DataGridIssueTypeByTypeManagerFilter,
  DaysWithHours,
  GridHeader,
  type ExtendedGridColDef,
} from '@issuemanager/apps/client/components';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import { IssueType } from '@issuemanager/constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IssueSeverityChip } from '../../../issue/components/issue-severity-chip';
import { SLAsGridSeverityFilter } from '../components/slas-grid/slas-grid-severity-filter';
import {
  SLA_FIELD_DAYS,
  SLA_FIELD_ESCALATION,
  SLA_FIELD_ISSUE_SEVERITY,
  SLA_FIELD_ISSUE_SUBTYPE,
  SLA_FIELD_ISSUE_TYPE,
  SLA_FIELD_MAX_ISSUE_DUE_DATE,
  SLA_FIELD_REMINDER,
} from '../constants';

const commonIssueTemplateGridColumnsConfig = {
  minWidth: 150,
};

export const useSLAsGridColumns = () => {
  const { t } = useTranslation('settings');
  const localizedIssueTypes = useLocalizedIssueTypes();

  const column = useCallback(
    (props: ExtendedGridColDef) => ({
      renderHeader: GridHeader,
      ...commonIssueTemplateGridColumnsConfig,
      ...props,
    }),
    [],
  );

  const columns = useMemo(
    () => [
      column({
        field: SLA_FIELD_ISSUE_TYPE,
        headerName: t('slaConfiguration.table.header.issueType'),
        valueGetter: (params) =>
          localizedIssueTypes[params.value.name as IssueType] ??
          params.value.name,
        renderHeaderFilter: DataGridIssueTypeByTypeManagerFilter,
      }),
      column({
        field: SLA_FIELD_ISSUE_SUBTYPE,
        headerName: t('slaConfiguration.table.header.issueSubtype'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: DataGridIssueSubtypeByTypeManagerFilter,
      }),
      column({
        field: SLA_FIELD_ISSUE_SEVERITY,
        headerName: t('slaConfiguration.table.header.severity'),
        renderCell: (params) => <IssueSeverityChip severity={params.value} />,
        renderHeaderFilter: SLAsGridSeverityFilter,
      }),
      column({
        field: SLA_FIELD_DAYS,
        headerName: t('slaConfiguration.table.header.days'),
        sortable: false,
        renderCell: (params) => <DaysWithHours days={params.value} />,
      }),
      column({
        field: SLA_FIELD_MAX_ISSUE_DUE_DATE,
        headerName: t('slaConfiguration.table.header.maxIssueDueDate'),
        sortable: false,
        renderCell: (params) => <DaysWithHours days={params.value} />,
      }),
      column({
        field: SLA_FIELD_ESCALATION,
        headerName: t('slaConfiguration.table.header.escalation'),
        sortable: false,
        renderCell: (params) => <DaysWithHours days={params.value} />,
      }),
      column({
        field: SLA_FIELD_REMINDER,
        headerName: t('slaConfiguration.table.header.reminder'),
        sortable: false,
        valueGetter: (params) => params.value.join('/'),
      }),
    ],
    [column, localizedIssueTypes, t],
  );

  return columns;
};
